import { useSelector } from "react-redux";
import { useState, useCallback, useRef, ChangeEvent } from "react";
import { TextField, InputAdornment } from "@mui/material";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { RootState } from "app/store";
import { type Asset } from "app/types";
import { usePickImageAsset } from "app/hook";
import { updateImageAssetMetadata, uploadImageFile } from "app/api";
import I18n from "common/i18n";
import { usePlaySound, dataURLToBlob } from "common/utils";
import { captureScreen } from "features/universal/captureScreen";
import { AssetPicker } from "./AssetPicker";
import { StampPicker } from "./StampPicker";
import { ChatInputBarProps, SelectedImage } from "./types";
import { MessageImagePreview } from "./MessageImagePreview";

const DEFAULT_SVG_SIZE = 512;

const canRasterizeSvg = () => typeof window !== "undefined" && typeof document !== "undefined";

const isSvgUrl = (url: string) => {
  if (!url) return false;
  if (url.startsWith("data:image/svg+xml")) return true;
  return /\.svg(?:\?.*)?$/i.test(url);
};

const parseSvgLength = (svgText: string, attr: "width" | "height") => {
  const match = svgText.match(new RegExp(`${attr}="([^"]+)"`, "i"));
  if (!match) return undefined;
  const raw = match[1].trim();
  if (!raw || raw.endsWith("%")) return undefined;
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : undefined;
};

const parseSvgViewBox = (svgText: string) => {
  const match = svgText.match(/viewBox="([^"]+)"/i);
  if (!match) return undefined;
  const parts = match[1]
    .trim()
    .split(/[\s,]+/)
    .map((part) => Number.parseFloat(part))
    .filter((value) => Number.isFinite(value));
  if (parts.length !== 4) return undefined;
  const width = parts[2];
  const height = parts[3];
  if (width <= 0 || height <= 0) return undefined;
  return { width, height };
};

const getSvgDimensions = (svgText: string) => {
  const widthAttr = parseSvgLength(svgText, "width");
  const heightAttr = parseSvgLength(svgText, "height");
  const viewBox = parseSvgViewBox(svgText);

  let width = widthAttr ?? viewBox?.width;
  let height = heightAttr ?? viewBox?.height;

  if (width && !height && viewBox) {
    height = Math.round((width * viewBox.height) / viewBox.width);
  } else if (height && !width && viewBox) {
    width = Math.round((height * viewBox.width) / viewBox.height);
  }

  return { width, height };
};

const loadSvgImage = (svgText: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    const svgBlob = new Blob([svgText], { type: "image/svg+xml" });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (error) => {
      URL.revokeObjectURL(url);
      reject(error);
    };
    img.src = url;
  });

const svgTextToPngDataUrl = async (svgText: string): Promise<string> => {
  const { width, height } = getSvgDimensions(svgText);
  const img = await loadSvgImage(svgText);

  const targetWidth = Math.max(1, Math.round(width ?? img.naturalWidth ?? DEFAULT_SVG_SIZE));
  const targetHeight = Math.max(1, Math.round(height ?? img.naturalHeight ?? DEFAULT_SVG_SIZE));

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas 2d context is not available");
  }
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
  return canvas.toDataURL("image/png");
};

const decodeSvgDataUrl = (dataUrl: string) => {
  const parts = dataUrl.split(",");
  if (parts.length < 2) return "";
  const header = parts[0];
  const data = parts.slice(1).join(",");
  if (header.includes(";base64")) {
    return atob(data);
  }
  return decodeURIComponent(data);
};

const fetchSvgText = async (url: string) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch svg: ${response.status}`);
  }
  return response.text();
};

const dataUrlToBlob = async (dataUrl: string) => {
  const response = await fetch(dataUrl);
  return response.blob();
};

const getPngObjectKeyFromUrl = (url: string) => {
  if (!url || url.startsWith("data:")) return undefined;
  try {
    const base = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const parsed = new URL(url, base);
    const objectKey = parsed.pathname.replace(/^\/+/, "");
    if (!/\.svg$/i.test(objectKey)) return undefined;
    return objectKey.replace(/\.svg$/i, ".png");
  } catch (error) {
    console.warn("Failed to parse asset url for png key:", error);
    return undefined;
  }
};

const getPngUrlFromSvgUrl = (url: string) => {
  if (!url) return undefined;
  if (!/\.svg(\?|$)/i.test(url)) return undefined;
  return url.replace(/\.svg(?=\?|$)/i, ".png");
};

const updateAssetMetadataCache = (
  queryClient: QueryClient,
  assetId: number,
  metadata: Record<string, string>,
) => {
  queryClient.setQueriesData(
    { queryKey: ["creator/getUserAssets"] },
    (oldData: { assets: Asset[]; totalSize: number } | undefined) => {
      if (!oldData || !Array.isArray(oldData.assets)) return oldData;
      return {
        ...oldData,
        assets: oldData.assets.map((asset) => {
          if (!asset || asset.id !== assetId) return asset;
          const existingMetadata =
            asset.metadata && typeof asset.metadata === "object" ? asset.metadata : {};
          return { ...asset, metadata: { ...existingMetadata, ...metadata } };
        }),
      };
    },
  );
};

const ensurePngUrlForImage = async (image: SelectedImage, queryClient: QueryClient) => {
  const metadata =
    image.metadata && typeof image.metadata === "object" ? image.metadata : undefined;
  const existingPngUrl = image.pngUrl ?? metadata?.png_url;
  if (existingPngUrl) return existingPngUrl;
  if (!isSvgUrl(image.url) || !canRasterizeSvg()) return image.url;

  const pngObjectKey = getPngObjectKeyFromUrl(image.url);
  const pngUrl = getPngUrlFromSvgUrl(image.url);
  if (!pngObjectKey || !pngUrl) return image.url;

  try {
    const svgText = image.url.startsWith("data:image/svg+xml")
      ? decodeSvgDataUrl(image.url)
      : await fetchSvgText(image.url);
    if (!svgText) return image.url;

    const pngDataUrl = await svgTextToPngDataUrl(svgText);
    const pngBlob = await dataUrlToBlob(pngDataUrl);

    await uploadImageFile({
      is_sync: false,
      fileBlob: pngBlob,
      filename: pngObjectKey,
      contentType: "image/png",
    });

    const nextMetadata = { ...(metadata ?? {}), png_url: pngUrl };
    await updateImageAssetMetadata({ id: image.id, metadata: nextMetadata });
    updateAssetMetadataCache(queryClient, image.id, nextMetadata);

    return pngUrl;
  } catch (error) {
    console.error("Failed to create png for chat asset:", error);
    return image.url;
  }
};

export const ChatInputBar = ({
  onSend,
  onStop,
  isTyping = false,
  disabled = false,
  placeholder,
  leadingButton,
  hideAssetButtons = false,
  showCaptureButton = false,
}: ChatInputBarProps) => {
  const play = usePlaySound();
  const queryClient = useQueryClient();
  const pickImageAsset = usePickImageAsset();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const user = useSelector((state: RootState) => state.user.active);
  const [value, setValue] = useState("");
  const [selectedImage, setSelectedImage] = useState<SelectedImage | null>(null);
  const [showStampPicker, setShowStampPicker] = useState(false);
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [capturing, setCapturing] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };

  const handleSend = useCallback(async () => {
    const trimmed = value.trim();
    // テキストまたは画像がある場合に送信可能
    if ((trimmed || selectedImage) && !isTyping) {
      play();
      const resolvedImageUrl = selectedImage
        ? await ensurePngUrlForImage(selectedImage, queryClient)
        : undefined;

      // textとimageUrlを一緒に渡す（分割しない）
      // タイプ判定（IMAGE / IMAGE_WITH_TEXT / TEXT）は呼び出し元で行う
      onSend(trimmed, resolvedImageUrl);

      setValue("");
      setSelectedImage(null);
      setShowStampPicker(false);
      setShowAssetPicker(false);
    }
  }, [value, selectedImage, isTyping, onSend, play, queryClient]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "Enter" && !e.shiftKey && !isTyping) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleToggleStampPicker = () => {
    play();
    setShowAssetPicker(false);
    setShowStampPicker((prev) => !prev);
  };

  const handleToggleAssetPicker = () => {
    play();
    setShowStampPicker(false);
    setShowAssetPicker((prev) => !prev);
  };

  const handleSelectImage = (image: SelectedImage) => {
    setSelectedImage(image);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
  };

  const handleCloseStampPicker = () => {
    setShowStampPicker(false);
  };

  const handleCloseAssetPicker = () => {
    setShowAssetPicker(false);
  };

  // 直接画像アップロード
  const handleDirectUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && user?.uid) {
      pickImageAsset(
        file,
        user.uid,
        (asset) => {
          setSelectedImage({ id: asset.id, url: asset.url, name: file.name });
          // 画像選択成功時にパネルを閉じる
          setShowStampPicker(false);
          setShowAssetPicker(false);
        },
        () => {
          // エラー時
        },
      );
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // いまの画面を撮って、カメラの直接アップロードと同じ経路で添付する
  const handleCaptureScreen = async () => {
    if (!user?.uid || capturing) return;
    play();
    setCapturing(true);
    try {
      const dataUrl = await captureScreen();
      if (!dataUrl) return;
      const file = new File([dataURLToBlob(dataUrl)], `screen-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });
      pickImageAsset(
        file,
        user.uid,
        (asset) => {
          setSelectedImage({ id: asset.id, url: asset.url, name: file.name });
          setShowStampPicker(false);
          setShowAssetPicker(false);
        },
        () => {
          // エラー時
        },
      );
    } finally {
      setCapturing(false);
    }
  };

  // テキストまたは画像がある場合に送信可能
  const isSendDisabled = isTyping || (!value.trim() && !selectedImage) || disabled;

  return (
    <div className="flex-col-el relative w-full">
      {/* スタンプピッカー */}
      {showStampPicker && (
        <StampPicker onSelect={handleSelectImage} onClose={handleCloseStampPicker} />
      )}

      {/* 画像ピッカーパネル */}
      {showAssetPicker && (
        <AssetPicker onSelect={handleSelectImage} onClose={handleCloseAssetPicker} />
      )}

      {/* 選択画像プレビュー */}
      {selectedImage && <MessageImagePreview image={selectedImage} onRemove={handleRemoveImage} />}

      {/* 入力エリア */}
      <div className="flex-col-el px-4 pt-2 pb-4">
        <div className="flex-row-el items-end gap-3">
          {showCaptureButton && (
            <button
              onClick={handleCaptureScreen}
              disabled={isTyping || disabled || capturing}
              aria-label={I18n.t("MSG_CHAT_CAPTURE_SCREEN_BTN")}
              className="type-inherit-font flex-col-el flex-center shrink-0 cursor-pointer border-2 border-solid active:opacity-70 disabled:opacity-50 rounded-[10px] w-[52px] h-[52px] transition-colors bg-white border-blue-60 hover:bg-blue-20"
            >
              <span className="material-symbols-outlined type-md text-blue-100">
                screenshot_monitor
              </span>
            </button>
          )}
          {leadingButton}
          {!hideAssetButtons && (
            <>
              {/* カメラボタン（直接アップロード） */}
              <div className="flex-col-el flex-center shrink-0 relative">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleDirectUpload}
                  className="absolute inset-0 opacity-0 cursor-pointer z-10"
                  disabled={isTyping || disabled}
                />
                <button
                  disabled={isTyping || disabled}
                  className="flex-col-el flex-center cursor-pointer border-2 border-solid active:opacity-70 disabled:opacity-50 rounded-[10px] w-[52px] h-[52px] transition-colors bg-white border-blue-60 hover:bg-blue-20"
                >
                  <span className="material-symbols-outlined !text-[28px] text-blue-100">
                    photo_camera
                  </span>
                </button>
              </div>

              {/* 画像選択ボタン（PREMIUM以外はクリック時にダイアログ表示） */}
              <button
                onClick={handleToggleAssetPicker}
                disabled={isTyping || disabled}
                className={`flex-col-el flex-center shrink-0 cursor-pointer border-2 border-solid active:opacity-70 disabled:opacity-50 rounded-[10px] w-[52px] h-[52px] transition-colors ${
                  showAssetPicker
                    ? "bg-blue-100 border-blue-100"
                    : "bg-white border-blue-60 hover:bg-blue-20"
                }`}
              >
                <span
                  className={`material-symbols-outlined !text-[28px] ${
                    showAssetPicker ? "text-white" : "text-blue-100"
                  }`}
                >
                  add_photo_alternate
                </span>
              </button>
            </>
          )}

          {/* 入力欄コンテナ + スタンプボタン内包 */}
          <div className="flex-col-el flex-1 min-w-0">
            <TextField
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                setShowStampPicker(false);
                setShowAssetPicker(false);
              }}
              placeholder={placeholder || I18n.t("MSG_ASSISTANT_PLACEHOLDER")}
              fullWidth
              multiline
              maxRows={5}
              variant="outlined"
              disabled={isTyping || disabled}
              InputProps={{
                sx: {
                  borderRadius: 4,
                  paddingX: 3,
                  paddingY: 1,
                  fontSize: 24,
                  fontFamily: "MPLUSRounded1c-Bold",
                  color: "#3F4A61",
                  backgroundColor: "white",
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <button
                      onClick={handleToggleStampPicker}
                      disabled={isTyping || disabled}
                      className={`flex-col-el flex-center cursor-pointer border-none p-1 rounded-lg transition-colors disabled:opacity-50 ${
                        showStampPicker ? "bg-green-100" : "bg-transparent hover:bg-green-20"
                      }`}
                    >
                      <span
                        className={`material-symbols-outlined !text-[28px] ${
                          showStampPicker ? "text-white" : "text-green-100"
                        }`}
                      >
                        sentiment_satisfied
                      </span>
                    </button>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          {/* 送信/停止ボタン */}
          {isTyping && onStop ? (
            <button
              onClick={onStop}
              className="flex-col-el flex-center shrink-0 cursor-pointer border-none active:opacity-70 bg-red-80 rounded-[10px] p-3"
            >
              <p className="text text-white">{I18n.t("MSG_ASSISTANT_STOP")}</p>
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={isSendDisabled}
              className="flex-col-el flex-center shrink-0 cursor-pointer border-none active:opacity-70 disabled:bg-gray-20 bg-safe rounded-[10px] p-3"
            >
              <p className="text text-white">{I18n.t("MSG_ASSISTANT_SEND")}</p>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
